import React, { useState } from "react";
import { X } from "lucide-react";
import { RxCross2 } from "react-icons/rx";

const defaultFormData = {
  title: "",
  cardImage: "",
  bannerImage: "",
  shortDescription: "",
  introText: "",
  isActive: true,

  sections: [],
};

const NewsFormModal = ({ mode, news, onClose, onSave, apiBaseUrl }) => {
  const [formData, setFormData] = useState(
    news
      ? JSON.parse(JSON.stringify(news))
      : JSON.parse(JSON.stringify(defaultFormData)),
  );

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };


  // ============================
  // Section Handlers
  // ============================

  const handleSectionChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedSections = [...prev.sections];

      updatedSections[index] = {
        ...updatedSections[index],
        [field]: value,
      };

      return {
        ...prev,
        sections: updatedSections,
      };
    });
  };

  const handleAddSection = () => {
    setFormData((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          heading: "",
          content: "",
        },
      ],
    }));
  };

  const handleRemoveSection = (index) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index),
    }));
  };

  // ============================
  // Submit Form
  // ============================

  const submitForm = async () => {
    try {
      setLoading(true);
      setError(null);

      let url;
      let method;

      if (mode === "add") {
        url = `${apiBaseUrl}/create`;
        method = "POST";
      } else {
        url = `${apiBaseUrl}/update/${news._id}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        console.log("Backend Error:", data);
        throw new Error(data.message || JSON.stringify(data));
      }

      console.log("Success:", data);

      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-6xl shadow-2xl my-8">
        {/* Header */}

        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === "add" ? "Add News & Event" : "Edit News & Event"}
          </h2>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 transition">
            <X size={24} />
          </button>
        </div>

        {/* Error */}

        {error && (
          <div className="flex justify-between items-center bg-red-100 border border-red-300 text-red-700 px-6 py-3">
            <span>{error}</span>

            <button onClick={() => setError(null)}>
              <RxCross2 />
            </button>
          </div>
        )}

        {/* Body */}

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* ================= BASIC INFORMATION ================= */}

          <div className="border rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold mb-6">Basic Information</h3>


              {/* Card Title */}

            <div className="mb-5">
              <label className="block font-medium mb-2">
                Card Title *
              </label>

              <input
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Card Title"
              />
            </div>

            {/* Title */}

            <div className="mb-5">
              <label className="block font-medium mb-2">Page Title *</label>

              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Page Title"
              />
            </div>

          

            {/* Intro */}

            <div className="mb-5">
              <label className="block font-medium mb-2">Page Introduction</label>

              <textarea
                rows="3"
                name="introText"
                value={formData.introText}
                onChange={handleFormChange}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                placeholder="Write introduction..."
              />
            </div>

            {/* Card Image */}

            <div className="mb-6">
              <label className="block font-medium mb-3">Card Image URL</label>

              <input
                type="text"
                name="cardImage"
                value={formData.cardImage}
                onChange={handleFormChange}
                placeholder="https://example.com/image.jpg"
                className="w-full border rounded-lg px-4 py-3"
              />

              {/* <p className="text-center my-3 text-gray-500">OR</p>

              <input
                type="file"
                accept="image/*"
                onChange={handleCardImageUpload}
              /> */}

              {formData.cardImage && (
                <img
                  src={formData.cardImage}
                  alt=""
                  className="mt-4 w-72 rounded-xl border shadow"
                />
              )}
            </div>

            {/* Banner Image */}

            <div>
              <label className="block font-medium mb-3">Banner Image URL</label>

              <input
                type="text"
                name="bannerImage"
                value={formData.bannerImage}
                onChange={handleFormChange}
                placeholder="https://example.com/banner.jpg"
                className="w-full border rounded-lg px-4 py-3"
              />

              {/* <p className="text-center my-3 text-gray-500">OR</p>

              <input
                type="file"
                accept="image/*"
                onChange={handleBannerImageUpload}
              /> */}

              {formData.bannerImage && (
                <img
                  src={formData.bannerImage}
                  alt=""
                  className="mt-4 w-full rounded-xl border shadow max-h-[280px] object-cover"
                />
              )}
            </div>
          </div>

          {/* ================= CONTENT SECTIONS ================= */}

          <div className="border rounded-xl p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">News Sections</h3>

              <button
                type="button"
                onClick={handleAddSection}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                + Add Section
              </button>
            </div>

            {formData.sections.length === 0 && (
              <div className="text-gray-500 text-center py-8 border-2 border-dashed rounded-lg">
                No Sections Added
              </div>
            )}

            {formData.sections.map((section, index) => (
              <div
                key={index}
                className="border rounded-xl p-5 mb-6 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-lg">Section {index + 1}</h4>

                  <button
                    type="button"
                    onClick={() => handleRemoveSection(index)}
                    className="bg-red-100 hover:bg-red-200 text-red-600 px-3 py-2 rounded-lg"
                  >
                    Remove
                  </button>
                </div>

                {/* Heading */}

                <div className="mb-4">
                  <label className="block font-medium mb-2">Heading</label>

                  <input
                    type="text"
                    value={section.heading}
                    onChange={(e) =>
                      handleSectionChange(index, "heading", e.target.value)
                    }
                    className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                    placeholder="Section Heading"
                  />
                </div>

                {/* Content */}

                <div>
                  <label className="block font-medium mb-2">Content</label>

                  <textarea
                    rows="6"
                    value={section.content}
                    onChange={(e) =>
                      handleSectionChange(index, "content", e.target.value)
                    }
                    className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                    placeholder="Write section content..."
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}

        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition font-semibold"
          >
            Cancel
          </button>

          <button
            onClick={submitForm}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : mode === "add"
                ? "Create News"
                : "Update News"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsFormModal;
